import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { openUrlInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { WorkerAvatar } from "@src/appV2/redesign/components/WorkerAvatar";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { formatDistanceToNow } from "date-fns";

interface ReviewAuthorNameProps {
  workerName: string;
  reviewUrl?: string;
}

function ReviewAuthorName(props: ReviewAuthorNameProps) {
  const { workerName, reviewUrl } = props;

  if (isDefined(reviewUrl)) {
    return (
      <TextButton
        sx={(theme) => ({
          height: "auto",
          padding: 0,
          color: theme.palette.text.primary,
          "& .MuiTypography-root": {
            fontWeight: theme.typography.fontWeightBold,
          },
        })}
        onClick={async (event) => {
          event.stopPropagation();
          await openUrlInBrowser(reviewUrl, "google-review-author");
        }}
      >
        {workerName}
      </TextButton>
    );
  }

  return (
    <Text semibold variant="body2">
      {workerName}
    </Text>
  );
}

interface WorkplaceReviewCommentAuthorInfoProps {
  workerName: string;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  createdAt: Date;
  shiftWorkerQualification?: string;
  useDelayedReviewLayout?: boolean;
  reviewUrl?: string;
}

export function WorkplaceReviewCommentAuthorInfo(props: WorkplaceReviewCommentAuthorInfoProps) {
  const {
    workerName,
    profilePictureUrl,
    isLoadingProfilePicture,
    createdAt,
    shiftWorkerQualification,
    useDelayedReviewLayout,
    reviewUrl,
  } = props;

  if (useDelayedReviewLayout) {
    return (
      <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ width: "100%" }}>
        <WorkerAvatar
          profilePictureUrl={profilePictureUrl}
          isLoading={isLoadingProfilePicture}
          size="small"
        />

        <Stack direction="row" justifyContent="space-between" sx={{ flex: 1 }}>
          <Box>
            <ReviewAuthorName workerName={workerName} reviewUrl={reviewUrl} />
            {isDefined(shiftWorkerQualification) && (
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {shiftWorkerQualification}
              </Text>
            )}
          </Box>

          <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
            {formatDistanceToNow(createdAt)} ago
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={3}>
      <WorkerAvatar
        profilePictureUrl={profilePictureUrl}
        isLoading={isLoadingProfilePicture}
        size="small"
      />

      <Box>
        <ReviewAuthorName workerName={workerName} reviewUrl={reviewUrl} />
        <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
          {formatDistanceToNow(createdAt)} ago
        </Text>
      </Box>
    </Stack>
  );
}
